import React from 'react';
import PropTypes from 'prop-types';
import './StatusBadge.css';

/**
 * StatusBadge Component
 * Displays a status badge with appropriate color and styling
 *
 * @param {string} status - The status value (e.g., 'twilio-approved', 'pending-review', 'not_created')
 * @param {string} text - Optional custom text to display (defaults to status)
 * @param {string} size - Size variant: 'small', 'medium', 'large' (default: 'medium')
 */
const StatusBadge = ({ status, text, size = 'medium' }) => {
  const normalizedStatus = status ? status.toLowerCase().replace(/_/g, '-') : 'unknown';

  // Map status to display text if not provided
  const getStatusText = () => {
    if (text) return text;

    const statusMap = {
      'twilio-approved': 'Approved',
      'approved': 'Approved',
      'verified': 'Approved',
      'pending-review': 'Pending Review',
      'in-review': 'In Review',
      'pending': 'Pending',
      'pending-trusthub': 'Business Profile in Review',
      'pending-a2p-trust-product': 'Trust Product in Review',
      'twilio-rejected': 'Rejected',
      'rejected': 'Rejected',
      'failed': 'Failed',
      'draft': 'Draft',
      'active': 'Active',
      'inactive': 'Inactive',
      'not-created': 'Not Created',
      'not-configured': 'Not Configured',
      'not-registered': 'Not Registered',
      'cancelled': 'Cancelled',
      'expired': 'Expired',
    };

    return statusMap[normalizedStatus] || status;
  };

  const displayText = getStatusText();

  // Determine text color based on status
  const getTextColorClass = () => {
    const greenStatuses = ['twilio-approved', 'approved', 'verified', 'active'];
    const redStatuses = ['twilio-rejected', 'rejected', 'failed', 'expired', 'cancelled', 'inactive'];
    const inReviewStatuses = ['in-review', 'pending-review', 'pending', 'pending-trusthub', 'pending-a2p-trust-product'];
    const draftStatuses = ['draft'];

    if (greenStatuses.includes(normalizedStatus)) return 'status-badge--green';
    if (redStatuses.includes(normalizedStatus)) return 'status-badge--red';
    if (inReviewStatuses.includes(normalizedStatus)) return 'status-badge--in-review';
    if (draftStatuses.includes(normalizedStatus)) return 'status-badge--draft';
    return 'status-badge--default';
  };

  const textColorClass = getTextColorClass();

  return (
    <span className={`status-badge status-badge--${size} ${textColorClass}`}>
      {displayText}
    </span>
  );
};

StatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
  text: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};

export default StatusBadge;
